import dayjs from 'dayjs';
import { debounce, get } from 'lodash';
import React, { useEffect, useEffectEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useClassActivities } from 'store/classActivities/selectors';
import { handleListClassActivitiesForStudentWithIds } from 'store/classActivities/thunks';
import { useCurrentClass } from 'store/classes/selectors';
import { useClassQuizzes } from 'store/classQuizzes/selectors';
import { handleListClassQuizzesForStudentWithIds } from 'store/classQuizzes/thunks';
import { useClassGameActivity } from 'store/one_game/selectors';
import { handleListClassGameActivityForStudentWithIds } from 'store/one_game/thunks';
import { useCurrentUser } from 'store/users/selectors';
import { useCollectionNotification } from 'utils/methods/firebaseNotification';

const useFetchUnseenNotifications = ({ filterNotificationData }) => {
    const classActivities = useClassActivities();
    const classQuizzes = useClassQuizzes();
    const { class_game_activity } = useClassGameActivity();
    const { updateNotificationsFetched } = useCollectionNotification();
    const currentUser = useCurrentUser();
    const currentClass = useCurrentClass();
    const dispatch = useDispatch();

    const fetchUnseenNotifications = useEffectEvent(async () => {
        try {
            const someUnSeen = filterNotificationData.filter((item) => {
                if (
                    item?.type === 'create_activity' ||
                    item?.type === 'update_activity' ||
                    item?.type === 'delete_activity' ||
                    item?.type === 'create_quiz' ||
                    item?.type === 'update_quiz' ||
                    item?.type === 'delete_quiz'
                ) {
                    const userSeen = get(item, 'userSeen', '');
                    const seenStatus = userSeen ? !userSeen.includes(currentUser?.id) : get(item, 'seen', '');
                    const isFetched = get(item, 'data.is_Fetched', false) || item?.is_fetched;
                    const isModifiedToday = Boolean(item?.modified) && dayjs(item.modified).isSame(dayjs(), 'day');
                    return isModifiedToday && seenStatus && !isFetched;
                }
                return false;
            });
            if ((currentUser?.role !== 'student') || (someUnSeen?.length === 0)) {
                return;
            }
            const activityUnseen = someUnSeen?.filter(
                (item) =>
                    item?.type === 'create_activity' ||
                    item?.type === 'update_activity' ||
                    item?.type === 'delete_activity',
            );
            const quizUnseen = someUnSeen?.filter(
                (item) =>
                    item?.type === 'delete_quiz' ||
                    item?.type === 'create_quiz' ||
                    item?.type === 'update_quiz',
            );
            const classActivityIds = new Set(
                (classActivities || []).map((item) => String(item?.activityId)),
            );
            const classGameActivityIds = new Set(
                (class_game_activity || []).map((item) => String(item?.activityId)),
            );
            const classQuizIds = new Set((classQuizzes || []).map((item) => String(item?.quizId)));
            const shouldFetchActivities = activityUnseen?.filter((activity) => {
                const activityId = String(activity?.data?.id || '');
                if (!activityId) return false;
                if (activity?.type === 'create_activity') {
                    return !classActivityIds.has(activityId);
                }
                if (activity?.type === 'delete_activity') {
                    return classActivityIds.has(activityId) || classGameActivityIds.has(activityId);
                }
                if (activity?.type === 'update_activity') {
                    return true;
                }
                return false;
            });
            const shouldFetchQuizzes = quizUnseen?.filter((quiz) => {
                const quizId = String(quiz?.data?.id || '');
                if (!quizId) return false;
                if (quiz?.type === 'create_quiz') {
                    return !classQuizIds.has(quizId);
                }
                if (quiz?.type === 'delete_quiz') {
                    return classQuizIds.has(quizId);
                }
                if (quiz?.type === 'update_quiz') {
                    return true;
                }
                return false;
            });

            const acitivityIds = shouldFetchActivities?.map((item) => item?.data?.id);
            const quizIds = shouldFetchQuizzes?.map((item) => item?.data?.id);
            const allActivityIds = [...new Set([...acitivityIds])];

            await Promise.all([
                shouldFetchActivities?.length > 0
                    ? dispatch(
                        handleListClassActivitiesForStudentWithIds(
                            currentClass.id,
                            currentUser?.id,
                            allActivityIds,
                        ),
                    )
                    : Promise.resolve(),
                shouldFetchQuizzes?.length > 0
                    ? dispatch(
                        handleListClassQuizzesForStudentWithIds(currentClass.id, currentUser?.id, quizIds),
                    )
                    : Promise.resolve(),
                shouldFetchActivities?.length > 0
                    ? dispatch(
                        handleListClassGameActivityForStudentWithIds(
                            currentClass.id,
                            currentUser?.id,
                            acitivityIds,
                        ),
                    )
                    : Promise.resolve(),
            ]);
            const fetchedNotificationDocIds = [
                ...new Set([
                    ...shouldFetchActivities.map((item) => item?.firebaseDocId || item?.id).filter(Boolean),
                    ...shouldFetchQuizzes.map((item) => item?.firebaseDocId || item?.id).filter(Boolean),
                ]),
            ];
            if (fetchedNotificationDocIds.length > 0) {
                await updateNotificationsFetched(fetchedNotificationDocIds);
            }
        } catch (error) {
            console.log(error, 'error');
        }
    }, [currentClass?.id, currentUser?.id, filterNotificationData, updateNotificationsFetched]);

    useEffect(() => {
        if (!currentClass?.id || !currentUser?.id || !Array.isArray(filterNotificationData)) {
            return;
        }

        const debouncedFetchClassActivities = debounce(fetchUnseenNotifications, 350);
        debouncedFetchClassActivities();

        return () => {
            debouncedFetchClassActivities.cancel();
        };
    }, [currentClass?.id, currentUser?.id, filterNotificationData]);

    return null;
};

export default useFetchUnseenNotifications;
